import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Phone, MessageCircle, Send, CheckCircle } from 'lucide-react';

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    setTimeout(() => setSubmitted(true), 100);
  };

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 islamic-pattern hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            {t('contact.title')}
          </h1>
          <p style={{ color: 'var(--text-secondary)' }} className="max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="space-y-8">
                <div>
                  <h3 className="text-gold text-sm font-semibold uppercase tracking-wider mb-6">
                    {language === 'sv' ? 'Kontaktuppgifter' : 'Contact Information'}
                  </h3>
                </div>

                <a href="mailto:info@quranonline.se" className="flex items-start gap-4 group">
                  <div className="w-12 h-12 rounded flex items-center justify-center transition-colors" style={{ border: '1px solid rgba(206, 150, 8, 0.3)' }}>
                    <Mail size={20} className="text-gold" />
                  </div>
                  <div>
                    <div className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>{language === 'sv' ? 'E-post (allmän)' : 'Email (general)'}</div>
                    <div className="group-hover:text-gold transition-colors" style={{ color: 'var(--text-primary)' }}>info@quranonline.se</div>
                  </div>
                </a>

                <a href="mailto:trial@quranonline.se" className="flex items-start gap-4 group">
                  <div className="w-12 h-12 rounded flex items-center justify-center transition-colors" style={{ border: '1px solid rgba(206, 150, 8, 0.3)' }}>
                    <Mail size={20} className="text-gold" />
                  </div>
                  <div>
                    <div className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>{language === 'sv' ? 'E-post (provlektion)' : 'Email (trial lesson)'}</div>
                    <div className="group-hover:text-gold transition-colors" style={{ color: 'var(--text-primary)' }}>trial@quranonline.se</div>
                  </div>
                </a>

                <a href="tel:+46733874447" className="flex items-start gap-4 group">
                  <div className="w-12 h-12 rounded flex items-center justify-center transition-colors" style={{ border: '1px solid rgba(206, 150, 8, 0.3)' }}>
                    <Phone size={20} className="text-gold" />
                  </div>
                  <div>
                    <div className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>{t('contact.info.phone')}</div>
                    <div className="group-hover:text-gold transition-colors" style={{ color: 'var(--text-primary)' }}>+46 73 387 44 47</div>
                  </div>
                </a>

                <a href="https://wa.me/46733874447" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                  <div className="w-12 h-12 rounded flex items-center justify-center transition-colors" style={{ border: '1px solid rgba(206, 150, 8, 0.3)' }}>
                    <MessageCircle size={20} className="text-gold" />
                  </div>
                  <div>
                    <div className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>WhatsApp</div>
                    <div className="group-hover:text-gold transition-colors" style={{ color: 'var(--text-primary)' }}>
                      +46 73 387 44 47
                    </div>
                  </div>
                </a>
              </div>
            </div>

            {/* Contact Form - Netlify */}
            <div className="lg:col-span-2">
              <div className="card-dark p-8">
                {submitted ? (
                  <div className="text-center py-12" data-testid="success-message">
                    <div className="w-16 h-16 border border-gold rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle size={32} className="text-gold" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                      {language === 'sv' ? 'Tack!' : 'Thank you!'}
                    </h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      {t('contact.success')}
                    </p>
                  </div>
                ) : (
                  <form 
                    name="contact"
                    method="POST"
                    data-netlify="true"
                    netlify-honeypot="bot-field"
                    onSubmit={handleSubmit}
                    data-testid="contact-form"
                  >
                    <input type="hidden" name="form-name" value="contact" />
                    <p className="hidden">
                      <label>Don't fill this out: <input name="bot-field" /></label>
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                          {t('contact.name')} *
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full h-12 px-4 rounded"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                          {t('contact.email')} *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full h-12 px-4 rounded"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                        {t('contact.phone')}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        className="w-full h-12 px-4 rounded"
                        data-testid="contact-phone"
                      />
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                        {t('contact.message')} *
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={6}
                        className="w-full px-4 py-3 rounded resize-none"
                        data-testid="contact-message"
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      className="btn-gold w-full md:w-auto flex items-center justify-center gap-2"
                      data-testid="contact-submit"
                    >
                      <Send size={18} />
                      {t('contact.send')}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
